import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ACCOUNT_SEGMENT_COUNT_MAX,
  ACCOUNT_SEGMENT_COUNT_MIN,
} from '@system-dyg/shared/utils';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  ErpAccountingAccountDto,
  ErpAccountingAccountFormSelects,
  ErpAccountingAccountParent,
  ErpAccountingAccountRequest,
  ErpAccountingAccountTreeNode,
  ErpAccountingCatalogOption,
  ErpAccountNumberConfiguration,
} from './erp-accounting-accounts-api.types';
import { ErpApiError, mapErpError } from './erp-api.errors';
import { ERP_API_URL } from './erp-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const COMPANIES_PATH = '/accounting/companies';

/** Estructura de cuenta y catálogo efectivo de una empresa en ERP. */
@Injectable({ providedIn: 'root' })
export class ErpAccountingAccountsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ERP_API_URL).replace(/\/+$/, '')}${COMPANIES_PATH}`;

  /** `null` cuando el GET 200 no trae `data`: la empresa no tiene estructura. */
  configuration(companyUuid: string): Promise<ErpAccountNumberConfiguration | null> {
    return this.read(
      this.http
        .get<unknown>(`${this.companyUrl(companyUuid)}/account-number-configuration`)
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      unwrapConfiguration,
    );
  }

  tree(companyUuid: string): Observable<ErpAccountingAccountTreeNode[]> {
    return this.http.get<unknown>(`${this.accountsUrl(companyUuid)}/tree`).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(body => unwrapTree(body)),
      catchError(error => throwError(() => mapAccountsError(error))),
    );
  }

  formSelects(companyUuid: string): Promise<ErpAccountingAccountFormSelects> {
    return this.read(
      this.http
        .get<unknown>(`${this.accountsUrl(companyUuid)}/form-selects`)
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      unwrapFormSelects,
    );
  }

  /** `excludeUuid` quita la cuenta en edición y sus descendientes. */
  parentSelects(
    companyUuid: string,
    excludeUuid?: string | null,
  ): Promise<ErpAccountingAccountParent[]> {
    let params = new HttpParams();
    if (excludeUuid) params = params.set('excludeUuid', excludeUuid);
    return this.read(
      this.http
        .get<unknown>(`${this.accountsUrl(companyUuid)}/parent-selects`, { params })
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      unwrapParents,
    );
  }

  getById(companyUuid: string, uuid: string): Promise<ErpAccountingAccountDto> {
    return this.read(
      this.http
        .get<unknown>(`${this.accountsUrl(companyUuid)}/${encodeURIComponent(uuid)}`)
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      unwrapAccount,
    );
  }

  create(
    companyUuid: string,
    payload: ErpAccountingAccountRequest,
  ): Promise<ErpAccountingAccountDto> {
    return this.read(
      this.http.post<unknown>(this.accountsUrl(companyUuid), payload),
      unwrapAccount,
    );
  }

  update(
    companyUuid: string,
    uuid: string,
    payload: ErpAccountingAccountRequest,
  ): Promise<ErpAccountingAccountDto> {
    return this.read(
      this.http.put<unknown>(
        `${this.accountsUrl(companyUuid)}/${encodeURIComponent(uuid)}`,
        payload,
      ),
      unwrapAccount,
    );
  }

  remove(companyUuid: string, uuid: string): Promise<void> {
    return this.read(
      this.http.delete<unknown>(
        `${this.accountsUrl(companyUuid)}/${encodeURIComponent(uuid)}`,
      ),
      () => undefined,
    );
  }

  private companyUrl(companyUuid: string): string {
    return `${this.baseUrl}/${encodeURIComponent(companyUuid)}`;
  }

  private accountsUrl(companyUuid: string): string {
    return `${this.companyUrl(companyUuid)}/accounting-accounts`;
  }

  private async read<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapAccountsError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds =
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      const jitterMs = Math.floor(Math.random() * 250);
      return timer(seconds * 1000 + jitterMs);
    }
    if (error.status >= 500 || error.status === 0) {
      return timer(300);
    }
  }
  return throwError(() => error);
}

function mapAccountsError(error: unknown): ErpApiError {
  const mapped = mapErpError(error);
  if (!(error instanceof HttpErrorResponse)) return mapped;
  const body = asRecord(error.error);
  if (!body) return mapped;
  const business = readString(body, 'error');
  const details = detailMessages(body['details']).filter(item => item !== business);
  if (business && details.length) {
    mapped.message = [business.replace(/\.$/, ''), ...details].join('. ');
  } else if (business) {
    mapped.message = business;
  } else if (details.length) {
    mapped.message = details.join('. ');
  }
  return mapped;
}

function unwrapConfiguration(body: unknown): ErpAccountNumberConfiguration | null {
  const record = asRecord(body);
  if (!record || !('data' in record)) return null;
  const data = record['data'];
  if (data === undefined || data === null) return null;

  const config = asRecord(data);
  const uuid = readString(config, 'uuid');
  const separator = readString(config, 'separator');
  const segmentCount = config?.['segmentCount'];
  if (
    !uuid ||
    !separator ||
    typeof segmentCount !== 'number' ||
    !Number.isInteger(segmentCount) ||
    segmentCount < ACCOUNT_SEGMENT_COUNT_MIN ||
    segmentCount > ACCOUNT_SEGMENT_COUNT_MAX
  ) {
    throw new ErpApiError('unexpected', 0);
  }
  return { uuid, segmentCount, separator, editable: config?.['editable'] === true };
}

function unwrapTree(body: unknown): ErpAccountingAccountTreeNode[] {
  const data = unwrapData(body);
  if (!Array.isArray(data)) throw new ErpApiError('unexpected', 0);
  return data.map(normalizeTreeNode);
}

function unwrapFormSelects(body: unknown): ErpAccountingAccountFormSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    types: catalogList(data['types']),
    natures: catalogList(data['natures']),
  };
}

function unwrapParents(body: unknown): ErpAccountingAccountParent[] {
  const data = unwrapData(body);
  if (!Array.isArray(data)) throw new ErpApiError('unexpected', 0);
  return data
    .map(normalizeParent)
    .filter((item): item is ErpAccountingAccountParent => item !== null);
}

function unwrapAccount(body: unknown): ErpAccountingAccountDto {
  const record = asRecord(unwrapData(body));
  const uuid = readString(record, 'uuid');
  const type = normalizeCatalog(record?.['type']);
  if (!record || !uuid || !type) throw new ErpApiError('unexpected', 0);
  return {
    uuid,
    companyUuid: readString(record, 'companyUuid') || null,
    source: normalizeCatalog(record['source']),
    editable: record['editable'] === true,
    accountNumber: readString(record, 'accountNumber'),
    formattedAccountNumber: readString(record, 'formattedAccountNumber'),
    name: readString(record, 'name'),
    type,
    nature: normalizeCatalog(record['nature']),
    parent: normalizeParent(record['parent']),
    createdAt: readString(record, 'createdAt') || undefined,
    updatedAt: readString(record, 'updatedAt') || undefined,
  };
}

function normalizeTreeNode(raw: unknown): ErpAccountingAccountTreeNode {
  const record = asRecord(raw);
  const uuid = readString(record, 'uuid');
  const type = normalizeCatalog(record?.['type']);
  if (!record || !uuid || !type) throw new ErpApiError('unexpected', 0);
  const children = record['children'];
  return {
    uuid,
    source: normalizeCatalog(record['source']),
    editable: record['editable'] === true,
    accountNumber: readString(record, 'accountNumber'),
    formattedAccountNumber: readString(record, 'formattedAccountNumber'),
    name: readString(record, 'name'),
    type,
    nature: normalizeCatalog(record['nature']),
    children: Array.isArray(children) ? children.map(normalizeTreeNode) : [],
  };
}

function normalizeParent(raw: unknown): ErpAccountingAccountParent | null {
  const record = asRecord(raw);
  const uuid = readString(record, 'uuid');
  if (!record || !uuid) return null;
  return {
    uuid,
    source: normalizeCatalog(record['source']),
    accountNumber: readString(record, 'accountNumber'),
    formattedAccountNumber: readString(record, 'formattedAccountNumber'),
    name: readString(record, 'name'),
    type: normalizeCatalog(record['type']),
  };
}

function catalogList(raw: unknown): ErpAccountingCatalogOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeCatalog)
    .filter((item): item is ErpAccountingCatalogOption => item !== null);
}

function normalizeCatalog(raw: unknown): ErpAccountingCatalogOption | null {
  const record = asRecord(raw);
  const uuid = readString(record, 'uuid');
  const name = readString(record, 'name');
  if (!uuid || !name) return null;
  return { uuid, name };
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function detailMessages(details: unknown): string[] {
  const record = asRecord(details);
  if (!record) return [];
  const messages: string[] = [];
  for (const value of Object.values(record)) {
    if (typeof value === 'string' && value.trim()) messages.push(value.trim());
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) messages.push(item.trim());
    }
  }
  return messages;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(record: Record<string, unknown> | null, key: string): string {
  const value = record?.[key];
  return typeof value === 'string' ? value.trim() : '';
}
